import React, { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { selfCheckinFetchGuestRoomAssign } from '@/apis/aipass'
import { SelfCheckinHotelInfo } from '../hotel-info'
import dayjs from 'dayjs'
import { isAllSameStayPeriod, StayPeriods } from '@/utils/stay-period'
import QRCode from 'qrcode.react'
import { SelfCheckinCompleteState } from '@/components/pages/self-checkin/complete'
import { RoomInfoType } from '@/models/self-checkin/card-reader'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'
import { ButtonType } from '@/components/atoms/button'
import { CompletionButton } from '@/components/molecules/self-checkin/completion-button'

type Props = {
  hotelId: string
  checkinId: string
  basicInfo: {
    hotelNameLatin: string
    telephone: string
    frontOpenTime: string
    frontCloseTime: string
  }
  onClickTop: () => void
  state: SelfCheckinCompleteState
}
export const AssignRoom: React.FC<Props> = ({ onClickTop, ...props }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const [roomInfo, setRoomInfo] = useState<RoomInfoType[]>()

  const backGroundGrayHeight = () => {
    return roomInfo && roomInfo.length ? containerHeightPercent : containerHeightViewHeight
  }

  // 1部屋でもQRコードで入室可能かを判定
  const hasQrCode = useMemo(() => {
    return roomInfo && roomInfo.length ? roomInfo.some(item => item.qrCode) : false
  }, [roomInfo])

  const fetchGuestRoomAssignInfo = async () => {
    await selfCheckinFetchGuestRoomAssign({
      hotelId: props.hotelId,
      reservationIdWithAssignIds: props.state.selectedReservationAndRooms,
      includeQrCode: true,
    })
      .then(res => {
        const assignRooms = props.checkinId ? Object.values(res).flat() : res
        assignRooms.sort((a, b) => {
          if (a.assignCheckinDate === b.assignCheckinDate) {
            return 0
          }
          return dayjs(a.assignCheckinDate).isBefore(dayjs(b.assignCheckinDate)) ? -1 : 1
        })
        setRoomInfo(assignRooms)
      })
      .catch(e => console.log(e))
  }

  useEffect(() => {
    fetchGuestRoomAssignInfo()
  }, [])

  // 部屋ごとの宿泊期間が全て同じかどうかを判定
  const isAllSameStayPeriods = useMemo(() => {
    if (!roomInfo) return false

    const stayPeriods: StayPeriods = roomInfo.map(item => ({
      roomCheckinDate: item.assignCheckinDate || '',
      roomCheckoutDate: item.assignCheckoutDate || '',
    }))

    return isAllSameStayPeriod(stayPeriods)
  }, [roomInfo])

  return (
    <div css={assignRoomPageStyle}>
      <div css={backGroundGrayHeight}>
        <div css={mainStyle}>
          <div css={listContainerStyle} className={hasQrCode ? 'has-qr-code' : 'without-qr-code'}>
            <div css={descriptionStyle}>
              {hasQrCode ? (
                <Trans i18nKey="Please take a picture of the screen below. You can enter with QR code.">
                  Please <span style={{ color: '#F2A40B' }}>take a picture</span> of the screen below. You can enter with QR code.
                </Trans>
              ) : (
                <Trans i18nKey="Please take a picture of your room and key number">
                  Please <span style={{ color: '#F2A40B' }}>take a picture</span> of your room and key number
                </Trans>
              )}
            </div>
            <p css={sendMailNotificationStyle}>
              {t('If you have already entered your e-mail address, room information will be sent to you')}
            </p>
            {roomInfo &&
              roomInfo.map((item, index) => (
                <div css={roomInfoListContainerStyle} className={item?.qrCode ? 'qr-room-info' : 'plain-room-info'} key={index}>
                  {item?.qrCode ? (
                    <div css={qrCodeContainerStyle}>
                      <QRCode value={item.qrCode} size={152} />
                      <div css={qrCodeRoomInfoContainerStyle}>
                        {!isAllSameStayPeriods && (
                          <div className="flexContainer" style={{ marginTop: '16px' }}>
                            <div css={smallTextStyle}>{t('SelfCheckIn.Guest room period')}</div>
                            <div css={roomNumberStyle}>
                              {item?.assignCheckinDate && item?.assignCheckoutDate
                                ? dayjs(item?.assignCheckinDate).format('MM/DD') + ' ~ ' + dayjs(item?.assignCheckoutDate).format('MM/DD')
                                : '-'}
                            </div>
                          </div>
                        )}
                        <div css={qrCodeRoomNumberContainerStyle}>
                          <div css={qrCodeSmallTextStyle}>{t('Room number')}</div>
                          <div>
                            <div css={qrcodeRoomTypeStyle}>
                              {language === 'ja' && (item?.typeNameJa || '-')}
                              {(language === 'en' || language === 'zh' || language === 'ko') && (item?.typeNameEn || '-')}
                            </div>
                            <div css={qrcodeContentStyle}>{item?.roomNumber || '-'}</div>
                          </div>
                        </div>
                        <div css={keyNumberContainerStyle}>
                          <div css={qrCodeSmallTextStyle}>{t('Key number')}</div>
                          <div css={qrcodeContentStyle}>{item?.keyNumber || '-'}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flexContainer">
                        <div css={smallTextStyle}>{t('Room type')}</div>
                        <div css={roomNumberStyle}>
                          {language === 'ja' && (item?.typeNameJa || '-')}
                          {(language === 'en' || language === 'zh' || language === 'ko') && (item?.typeNameEn || '-')}
                        </div>
                      </div>

                      {/* 宿泊期間 */}
                      {/* 客室の期間がひとつでも異なる場合は、宿泊期間を表示 */}
                      {!isAllSameStayPeriods && (
                        <div className="flexContainer">
                          <div css={smallTextStyle}>{t('SelfCheckIn.Guest room period')}</div>
                          <div css={roomNumberStyle}>
                            {item?.assignCheckinDate && item?.assignCheckoutDate
                              ? dayjs(item?.assignCheckinDate).format('MM/DD') + ' ~ ' + dayjs(item?.assignCheckoutDate).format('MM/DD')
                              : '-'}
                          </div>
                        </div>
                      )}

                      <div className="flexContainer">
                        <div css={smallTextStyle}>{t('Room number')}</div>
                        <div css={roomNumberStyle}>{item?.roomNumber || '-'}</div>
                      </div>

                      <div className="flexContainer">
                        <div css={smallTextStyle}>{t('Key number')}</div>
                        <div css={roomNumberStyle}>{item?.keyNumber || '-'}</div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            {(!roomInfo || roomInfo?.length === 0) && (
              <div css={roomInfoListContainerStyle} className="plain-room-info">
                <div className="flexContainer">
                  <div css={smallTextStyle}>{t('Room type')}</div>
                  <div css={roomNumberStyle}>-</div>
                </div>

                <div className="flexContainer">
                  <div css={smallTextStyle}>{t('Room number')}</div>
                  <div css={roomNumberStyle}>-</div>
                </div>

                <div className="flexContainer">
                  <div css={smallTextStyle}>{t('Key number')}</div>
                  <div css={roomNumberStyle}>-</div>
                </div>
              </div>
            )}
            <div css={roomGuidanceConfirmStyleButton}>
              <CompletionButton buttonType={ButtonType.Secondary} onClick={onClickTop}>
                {hasQrCode ? t('SelfCheckIn.Photo taken') : t('Room and key number confirmed')}
              </CompletionButton>
            </div>
          </div>
        </div>
        <SelfCheckinHotelInfo
          telephone={props.basicInfo.telephone}
          frontOpenTime={props.basicInfo.frontOpenTime}
          frontCloseTime={props.basicInfo.frontCloseTime}
          position={'static'}
          usedIn="roomGuidance"
        />
      </div>
    </div>
  )
}

const assignRoomPageStyle = css({
  width: '100%',
  minHeight: '100vh',
  background: '#F2F2F2',
})

const mainStyle = css(
  { paddingTop: 116 },
  createSelfCheckinResponsiveStyle({
    portrait: { paddingTop: 113 },
    landscape13: { paddingTop: 118 },
  }),
)

const descriptionStyle = css(
  {
    paddingTop: 40,
    color: '#272727',
    fontWeight: 'bold',
    fontSize: 22,
    lineHeight: '30px',
    letterSpacing: 1.1,
    textAlign: 'center',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 26, lineHeight: '36px', letterSpacing: 1.3 },
  }),
)

const roomInfoListContainerStyle = css(
  {
    boxSizing: 'border-box',
    border: '2px solid #CCCCCC',
    borderRadius: 10,
    background: '#F5F5F5',
    margin: '32px 50px 0',
    '.flexContainer': {
      display: 'flex',
    },
    // 部屋番号が長く折り返しても行同士が重ならないよう、行の高さは内容に委ねて余白はgapで確保する
    '&.plain-room-info': {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      padding: '30px 0 26px',
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: { margin: '32px 40px 0' },
    landscape13: { marginTop: 31 },
  }),
)

// 部屋番号が長い場合もラベル幅を保ち、部屋タイプ・部屋番号・鍵番号の値の開始位置を揃える
const smallTextStyle = css(
  {
    boxSizing: 'border-box',
    flexShrink: 0,
    width: 164,
    paddingLeft: 48,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#676767',
    letterSpacing: 1.6,
    lineHeight: 'normal',
    paddingTop: 8,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { width: 174, paddingLeft: 58 },
    landscape13: { width: 184, paddingLeft: 48, fontSize: 20, lineHeight: 'normal', letterSpacing: 1 },
  }),
)

const roomNumberStyle = css(
  {
    minWidth: 0,
    overflowWrap: 'break-word',
    fontSize: 28,
    fontWeight: 'bold',
    letterSpacing: 1.4,
    lineHeight: 'normal',
    textAlign: 'left',
    color: '#f2a40b',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 32, lineHeight: 'normal', letterSpacing: 1.6 },
  }),
)

const listContainerStyle = css(
  {
    boxSizing: 'border-box',
    borderRadius: 10,
    background: '#FFF',
    minHeight: 466,
    padding: '0 0 30px 0',
    overflow: 'hidden',
    '&.has-qr-code': { minHeight: 472 },
  },
  createSelfCheckinResponsiveStyle({
    portrait: { borderRadius: 0 },
    landscape13: { minHeight: 500, '&.has-qr-code': { minHeight: 491 } },
  }),
)

const roomGuidanceConfirmStyleButton = css(
  {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 38,
    '.has-qr-code &': { marginTop: 32 },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { marginTop: 32 },
  }),
)

const sendMailNotificationStyle = css(
  {
    textAlign: 'center',
    color: '#676767',
    margin: '16px 0 0',
    fontSize: 16,
    letterSpacing: 0.8,
    lineHeight: '22px',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { marginTop: 15, fontSize: 20, lineHeight: '28px', letterSpacing: 1 },
  }),
)

const containerHeightPercent = css(
  {
    margin: '0 auto',
    width: '90%',
    maxWidth: 'none',
    minHeight: '100vh',
  },
  createSelfCheckinResponsiveStyle({
    portrait: { width: '100%' },
    landscape13: { width: 1230 },
  }),
)

const containerHeightViewHeight = css(
  {
    margin: '0 auto',
    width: '90%',
    maxWidth: 'none',
    minHeight: '100vh',
  },
  createSelfCheckinResponsiveStyle({
    portrait: { width: '100%', height: 'auto', minHeight: '100vh' },
    landscape13: { width: 1230 },
  }),
)

const qrCodeContainerStyle = css({
  display: 'flex',
  gap: '32px',
  padding: '22px 24px',
})

const qrCodeRoomInfoContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '16px',
})

const qrCodeRoomNumberContainerStyle = css({
  display: 'flex',
  alignItems: 'flex-end',
})

const qrcodeContentStyle = css(
  {
    minWidth: 0,
    overflowWrap: 'break-word',
    fontSize: 28,
    fontWeight: 'bold',
    letterSpacing: 1.4,
    lineHeight: '36px',
    textAlign: 'left',
    color: '#f2a40b',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 32, lineHeight: '44px', letterSpacing: 1.6 },
  }),
)

const qrcodeRoomTypeStyle = css(
  {
    fontSize: 16,
    fontWeight: 'bold',
    letterSpacing: 0.8,
    lineHeight: '22px',
    color: '#f2a40b',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 20, lineHeight: '28px', letterSpacing: 1 },
  }),
)

const qrCodeSmallTextStyle = css(
  {
    flexShrink: 0,
    width: 99,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#676767',
    letterSpacing: 1.6,
    lineHeight: '36px',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { width: 115, fontSize: 20, lineHeight: '44px', letterSpacing: 2 },
  }),
)

const keyNumberContainerStyle = css({
  display: 'flex',
})
